import {Calendar} from 'primereact/calendar';
import {classNames} from 'primereact/utils';
import React, {useEffect, useRef, useState} from 'react';
import InfoTooltipTemplate from '../InfoTooltipTemplate';

/**
 * A customizable date input template
 */
const DateInputTemplate = ({
                               appendTo = "self",
                               name,
                               label,
                               disabled,
                               className,
                               labelClassName,
                               floatLabel,
                               noMargins,
                               bottomMargin,
                               inline,
                               value,
                               placeholder,
                               onChangeFunction,
                               isValid,
                               errorMessage,
                               focusInput,
                               selectInput,
                               category,
                               infoMessage,
                               selectionMode = 'single'
                           }) => {
    const calendarRef = useRef();
    const noSpaceRangeRegex = /^\d{2}\/\d{2}\/\d{4}-\d{2}\/\d{2}\/\d{4}$/;
    const [inputClasses, setInputClasses] = useState(classNames({
        'p-invalid': isValid,
        'w-full': true
    }));
    const [labelClasses, setLabelClasses] = useState(classNames({
        'p-error': isValid,
        'w-full': true,
        'block mb-2': !inline,
        'mr-2': inline,
        'pr-4': infoMessage
    }));

    const normalizeAndParseInput = (raw: string): [Date | null, Date | null] => {
        if (!raw) return [null, null];

        // Normalize: Ensure space around dash
        const normalized = raw.replace(/(\d{2}\/\d{2}\/\d{4})-(\d{2}\/\d{2}\/\d{4})/, '$1 - $2');

        const parts = normalized.split(' - ');
        if (parts.length !== 2) return [null, null];

        const [startStr, endStr] = parts;
        const [start, end] = [new Date(startStr.split('/').reverse().join('/')), new Date(endStr.split('/').reverse().join('/'))];

        return [isNaN(start.getTime()) ? null : start, isNaN(end.getTime()) ? null : end];
    };

    useEffect(() => {
        // add input classes (if we have any)
        if (className) {
            setInputClasses(inputClasses + ' ' + className);
        }

        // add label classes (if we have any)
        if (labelClassName) {
            setLabelClasses(labelClasses + ' ' + labelClassName);
        }
        setTimeout(() => {
            // focus on input on load
            if (focusInput) {
                calendarRef.current.focus();
            }

            // select all input on load
            if (selectInput) {
                calendarRef.current.select();
            }
        }, 50);
    }, []);

    const inputField = (
        <div className="relative" onClick={() => calendarRef?.current?.focus()}>
            <Calendar
                inputRef={calendarRef}
                appendTo={appendTo}
                id={name}
                value={value}
                disabled={disabled}
                placeholder={placeholder}
                onChange={(e) => {
                    onChangeFunction(name, e.value, category);
                }}
                onInput={(e) => {
                    const rawValue = e.target.value;
                    if (noSpaceRangeRegex.test(rawValue)) {
                        const x = normalizeAndParseInput(rawValue)
                        onChangeFunction(name, x, category);
                    }
                }}
                className={inputClasses}
                selectOtherMonths
                dateFormat="dd/mm/yy"
                selectionMode={selectionMode}
                showButtonBar
            />
            <div className="calendar-icon-wrap">
                <i onClick={() => calendarRef?.current?.focus()} className="pi pi-calendar"/>
            </div>
        </div>
    );

    return (
        <div className={(!noMargins ? ('mb-' + (bottomMargin || '5')) : '') + (infoMessage ? ' relative' : '')}>
            {floatLabel
                ? <>
                    <span className="p-float-label">
                        {inputField}
                        <label htmlFor={name} className={labelClasses}
                               title={labelClassName == 'one-line' ? label : null}>{label}</label>
                        {infoMessage && <div className="top-right-icon">
                            <InfoTooltipTemplate target={'tooltip-' + name} message={infoMessage} className="ml-2"/>
                        </div>}
                    </span>

                    {errorMessage}
                </>

                : <>
                    <label htmlFor={name} className={labelClasses}
                           title={labelClassName == 'one-line' ? label : null}>{label}</label>
                    {infoMessage && <div className="top-right-icon">
                        <InfoTooltipTemplate target={'tooltip-' + name} message={infoMessage} className="ml-2"/>
                    </div>}
                    {inputField}
                    {errorMessage}
                </>
            }
        </div>
    );
};

export default DateInputTemplate;